import {
  useEffect,
  useRef
} from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';

import Pagination from '@northern.tech/common-ui/Pagination';

export type ActiveAlertItem = {
  connectionStatus: string;
  deviceId: string;
  deviceName: string;
  firstAbnormalTime: string;
  programStatus: string;
};

type ActiveAlertsProps = {
  alerts: ActiveAlertItem[];
  onChangePagination: (
    newPage: number,
    newRowsPerPage?: number
  ) => void;
  onClose: () => void;
  open: boolean;
  page: number;
  rowsPerPage: number;
  total: number;
};

/*
 * Active Alerts UI settings.
 *
 * Keep visual settings here so the popup can be adjusted easily
 * during the final UI tuning phase.
 */
const ACTIVE_ALERTS_DIALOG_WIDTH = 920;
const ACTIVE_ALERTS_DIALOG_HEIGHT = '70vh';

const ACTIVE_ALERT_VALUE_COLOR = '#d32f2f';

const isNormalConnection = (status: string) =>
  status === 'CONNECT';

const isAbnormalConnection = (status: string) =>
  status === 'DISCONNECT';

const isNormalProgram = (status: string) =>
  status === 'SUCCESS';

const isAbnormalProgram = (status: string) =>
  status === 'STOPPED' || status === 'UNKNOWN';

const ActiveAlerts = ({
  alerts,
  onChangePagination,
  onClose,
  open,
  page,
  rowsPerPage,
  total
}: ActiveAlertsProps) => {
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  /*
   * Return the table area to the top whenever the page or the
   * number of rows per page changes.
   */
  useEffect(() => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTop = 0;
    }
  }, [page, rowsPerPage]);

  return (
    <Dialog
      maxWidth={false}
      open={open}
      PaperProps={{
        style: {
          height: ACTIVE_ALERTS_DIALOG_HEIGHT,
          maxHeight: ACTIVE_ALERTS_DIALOG_HEIGHT,
          width: `${ACTIVE_ALERTS_DIALOG_WIDTH}px`
        }
      }}
      onClose={onClose}
    >
      <DialogTitle
        style={{
          flexShrink: 0,
          fontSize: '22px',
          fontWeight: 600,
          paddingRight: '56px'
        }}
      >
        Active Alerts ({total})

        <IconButton
          aria-label="Close Active Alerts"
          size="small"
          style={{
            position: 'absolute',
            right: '16px',
            top: '14px'
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0
        }}
      >
        {total === 0 ? (
          <div
            className="muted"
            style={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              padding: '32px',
              textAlign: 'center'
            }}
          >
            No active alerts.
          </div>
        ) : (
          <>
            <div
              ref={tableScrollRef}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto'
              }}
            >
              <Table
                stickyHeader
                style={{
                  tableLayout: 'fixed',
                  width: '100%'
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: '24%'
                      }}
                    >
                      Device name
                    </TableCell>

                    <TableCell
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: '26%'
                      }}
                    >
                      Device ID
                    </TableCell>

                    <TableCell
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: '14%'
                      }}
                    >
                      Connection
                    </TableCell>

                    <TableCell
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: '14%'
                      }}
                    >
                      Program
                    </TableCell>

                    <TableCell
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: '22%'
                      }}
                    >
                      First seen
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {alerts.map(alert => (
                    <TableRow
                      key={alert.deviceId}
                      hover
                    >
                      <TableCell>
                        <span
                          title={alert.deviceName}
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {alert.deviceName}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          title={alert.deviceId}
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {alert.deviceId}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          className={
                            isNormalConnection(alert.connectionStatus)
                              ? 'green'
                              : undefined
                          }
                          style={{
                            color: isAbnormalConnection(
                              alert.connectionStatus
                            )
                              ? ACTIVE_ALERT_VALUE_COLOR
                              : undefined,
                            fontWeight: 600
                          }}
                        >
                          {alert.connectionStatus}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          className={
                            isNormalProgram(alert.programStatus)
                              ? 'green'
                              : undefined
                          }
                          style={{
                            color: isAbnormalProgram(
                              alert.programStatus
                            )
                              ? ACTIVE_ALERT_VALUE_COLOR
                              : undefined,
                            fontWeight: 600
                          }}
                        >
                          {alert.programStatus}
                        </span>
                      </TableCell>

                      <TableCell
                        style={{
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {alert.firstAbnormalTime}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div
              style={{
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                flexShrink: 0,
                padding: '12px 16px'
              }}
            >
              <Pagination
                className="margin-top-none"
                count={total}
                rowsPerPage={rowsPerPage}
                onChangePage={newPage =>
                  onChangePagination(
                    newPage,
                    rowsPerPage
                  )
                }
                onChangeRowsPerPage={newRowsPerPage =>
                  onChangePagination(
                    1,
                    newRowsPerPage
                  )
                }
                page={page}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActiveAlerts;